import { notification } from 'antd';
import axios, {
  type AxiosInstance,
  type AxiosRequestConfig,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from 'axios';

import {
  createMockConnectorClient,
  createMockCycloneClient,
  createMockGenericClient,
  createMockMLflowClient,
  createMockOpenMetadataClient,
  createMockQualityClient,
  createMockSystemClient,
} from '@/shared/api/demo/mockClients';
import { AUTH_ROUTES, KEYBOARD_KEYS, NOTIFICATION_CONFIG } from '@/shared/lib/constants';
import { HTTP_STATUS, REQUEST_TIMEOUT } from '@/shared/lib/constants/api';
import { emitSessionExpired } from '@/shared/lib/events/sessionExpired';
import { performanceMonitor } from '@/shared/lib/monitoring/performanceMetrics';
import { getIsSessionExpired } from '@/shared/lib/session';

import { API_ERROR_MESSAGES } from './errorMessages';

declare module 'axios' {
  export interface InternalAxiosRequestConfig {
    metadata?: {
      startTime?: number;
    };
    _retry?: boolean;
    skipGlobalErrorNotification?: boolean;
  }

  export interface AxiosRequestConfig {
    skipGlobalErrorNotification?: boolean;
  }
}

const tagMutuallyExclusiveErrorPattern = /^Tag labels (.+?) are/;

/**
 * 성능 모니터링 시 저장하지 않아야 할 민감한 헤더 목록
 * 토큰, 인증 정보, 쿠키 등 보안상 중요한 정보 제외
 */
const SENSITIVE_HEADERS = [
  'authorization',
  'cookie',
  'set-cookie',
  'x-api-key',
  'x-auth-token',
  'x-csrf-token',
  'x-xsrf-token',
  'proxy-authorization',
] as const;

/**
 * 민감한 헤더를 제외한 헤더 키 목록만 반환
 * 값을 제외하고 키 목록만 저장하여 보안 위험 최소화
 */
const sanitizeHeaders = (headers: Record<string, unknown> = {}): string[] => {
  return Object.keys(headers).filter((key) => !SENSITIVE_HEADERS.includes(key.toLowerCase() as never));
};

/**
 * 요청/응답 데이터의 메타정보만 추출
 * 실제 데이터 대신 타입, 크기 등의 메타정보만 저장하여 민감 정보 유출 방지
 */
const getDataMetadata = (data: unknown): { type: string; isArray: boolean; size: number } | null => {
  if (data === null || data === undefined) return null;
  try {
    return {
      type: typeof data,
      isArray: Array.isArray(data),
      size: JSON.stringify(data).length,
    };
  } catch {
    return { type: typeof data, isArray: false, size: 0 };
  }
};

/**
 * 브라우저 환경에서만 실행되도록 함수를 래핑하는 고차 함수
 * 테스트 환경(Node.js)에서 notification API 호출 시 발생하는 에러 방지
 */
const withBrowserCheck = <T extends (...args: never[]) => void>(fn: T): ((...args: Parameters<T>) => void) => {
  return (...args: Parameters<T>) => {
    if (typeof window !== 'undefined') {
      fn(...args);
    }
  };
};

/**
 * 테스트 환경에서 안전하게 notification을 표시하는 헬퍼
 * key를 사용하여 동일한 에러의 중복 알림 방지
 */
const safeNotification = {
  error: withBrowserCheck((args: Parameters<typeof notification.error>[0]) => {
    notification.error({ key: args.key || 'error', ...args });
  }),
  warning: withBrowserCheck((args: Parameters<typeof notification.warning>[0]) => {
    notification.warning({ key: args.key || 'warning', ...args });
  }),
  success: withBrowserCheck((args: Parameters<typeof notification.success>[0]) => {
    notification.success({ key: args.key || 'success', ...args });
  }),
  info: withBrowserCheck((args: Parameters<typeof notification.info>[0]) => {
    notification.info({ key: args.key || 'info', ...args });
  }),
};

/**
 * 우리 API 엔드포인트인지 확인
 */
const API_PREFIXES = ['/backend/api/v1', '/catalog/api/v1', '/connector', '/api/v1/dags'] as const;

const isOurApiEndpoint = (baseURL?: string, url?: string): boolean => {
  const urlString = String(url || '');
  return API_PREFIXES.some((prefix) => baseURL?.startsWith(prefix) || urlString.startsWith(prefix));
};

/**
 * 세션 만료 여부 확인 (401 상태 코드)
 */
const isSessionExpired = (status: number): boolean => {
  return status === HTTP_STATUS.UNAUTHORIZED;
};

/**
 * 공통 에러 처리 함수
 * 모든 API 클라이언트에서 사용하는 표준 에러 핸들러
 */
const handleApiError = async (error: unknown) => {
  if (!axios.isAxiosError(error)) {
    return Promise.reject(error);
  }

  if (!error.response) {
    if (error.code === 'ERR_NETWORK') {
      const baseURL = error.config?.baseURL;
      const url = error.config?.url;

      if (isOurApiEndpoint(baseURL, url)) {
        if (getIsSessionExpired()) {
          console.warn('[handleApiError] ⏸️ ERR_NETWORK - 이미 세션 만료됨 → 조용히 실패', {
            baseURL,
            url,
          });
          return Promise.reject(error);
        }

        console.warn('[handleApiError] ⚠️ ERR_NETWORK 발생 (세션 체크는 useSessionCheck에서 자동 처리)', {
          baseURL,
          url,
        });
      } else {
        safeNotification.error({
          key: 'network-error',
          ...API_ERROR_MESSAGES.NETWORK_ERROR,
          placement: NOTIFICATION_CONFIG.PLACEMENT,
        });
      }
    } else if (error.code === 'ECONNABORTED') {
      safeNotification.error({
        key: 'timeout-error',
        ...API_ERROR_MESSAGES.TIMEOUT,
        placement: NOTIFICATION_CONFIG.PLACEMENT,
      });
    }
    return Promise.reject(error);
  }

  const status = error.response.status;

  if (isSessionExpired(status)) {
    console.warn('[handleApiError] 🚨 401 응답 감지 → 세션 만료 이벤트 발생');
    emitSessionExpired();
    return Promise.reject(error);
  }

  // skipGlobalErrorNotification 플래그가 설정된 경우 전역 notification 건너뛰기
  const skipNotification = error.config?.skipGlobalErrorNotification;

  if (!skipNotification) {
    if (status === HTTP_STATUS.FORBIDDEN) {
      safeNotification.error({
        key: 'forbidden',
        ...API_ERROR_MESSAGES.FORBIDDEN,
        placement: NOTIFICATION_CONFIG.PLACEMENT,
      });
    } else if (status === HTTP_STATUS.NOT_FOUND) {
      safeNotification.error({
        key: 'not-found',
        ...API_ERROR_MESSAGES.NOT_FOUND,
        placement: NOTIFICATION_CONFIG.PLACEMENT,
      });
    } else if (status >= HTTP_STATUS.INTERNAL_SERVER_ERROR) {
      safeNotification.error({
        key: 'server-error',
        ...API_ERROR_MESSAGES.SERVER_ERROR,
        placement: NOTIFICATION_CONFIG.PLACEMENT,
      });
    } else if (status === HTTP_STATUS.BAD_REQUEST) {
      safeNotification.error({
        key: 'bad-request',
        message: API_ERROR_MESSAGES.BAD_REQUEST.message,
        description: error.response.data?.message || API_ERROR_MESSAGES.BAD_REQUEST.description,
        placement: NOTIFICATION_CONFIG.PLACEMENT,
      });
    }
  }

  return Promise.reject(error);
};

const commonAxiosConfig = {
  timeout: REQUEST_TIMEOUT.DEFAULT,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
};

const IS_DEMO_MODE = import.meta.env.MODE === 'demo';

type AxiosMethodName = 'delete' | 'get' | 'patch' | 'post' | 'put';

const isMockFallbackResponse = (response: AxiosResponse): boolean => {
  const message = typeof response.data?.message === 'string' ? response.data.message : '';

  return (
    response.status === 501 || (response.status === HTTP_STATUS.NOT_FOUND && message.startsWith('Mock not implemented'))
  );
};

const createHybridMockClient = (mockClient: Partial<AxiosInstance>, realClient: AxiosInstance): AxiosInstance => {
  const callWithFallback = async <T = unknown, R = AxiosResponse<T>>(
    method: AxiosMethodName,
    args: unknown[]
  ): Promise<R> => {
    const mockMethod = mockClient[method] as ((...methodArgs: unknown[]) => Promise<AxiosResponse<T>>) | undefined;

    if (mockMethod) {
      const mockResponse = await mockMethod(...args);

      if (!isMockFallbackResponse(mockResponse)) {
        return mockResponse as R;
      }
    }

    const realMethod = realClient[method] as (...methodArgs: unknown[]) => Promise<R>;
    return realMethod(...args);
  };

  return {
    ...realClient,
    delete<T = unknown, R = AxiosResponse<T>, D = unknown>(url: string, config?: AxiosRequestConfig<D>): Promise<R> {
      return callWithFallback<T, R>('delete', [url, config]);
    },
    get<T = unknown, R = AxiosResponse<T>, D = unknown>(url: string, config?: AxiosRequestConfig<D>): Promise<R> {
      return callWithFallback<T, R>('get', [url, config]);
    },
    patch<T = unknown, R = AxiosResponse<T>, D = unknown>(
      url: string,
      data?: D,
      config?: AxiosRequestConfig<D>
    ): Promise<R> {
      return callWithFallback<T, R>('patch', [url, data, config]);
    },
    post<T = unknown, R = AxiosResponse<T>, D = unknown>(
      url: string,
      data?: D,
      config?: AxiosRequestConfig<D>
    ): Promise<R> {
      return callWithFallback<T, R>('post', [url, data, config]);
    },
    put<T = unknown, R = AxiosResponse<T>, D = unknown>(
      url: string,
      data?: D,
      config?: AxiosRequestConfig<D>
    ): Promise<R> {
      return callWithFallback<T, R>('put', [url, data, config]);
    },
  } as AxiosInstance;
};

const realQualityApiClient = axios.create({
  ...commonAxiosConfig,
  baseURL: '/backend/api/v1',
});

realQualityApiClient.interceptors.response.use((response) => response, handleApiError);

const realSystemApiClient = axios.create({
  ...commonAxiosConfig,
  baseURL: '/backend/api/v1',
});

realSystemApiClient.interceptors.response.use((response) => response, handleApiError);

const realAuthApiClient = axios.create(commonAxiosConfig);

realAuthApiClient.interceptors.response.use((response) => {
  const contentType = response.headers['content-type'];
  if (contentType && contentType.includes('text/html')) {
    const currentPath = window.location.pathname;
    console.warn('[authApiClient] HTML 응답 감지:', {
      url: response.config.url,
      currentPath,
      contentType,
    });

    const error = new Error('API가 HTML 응답을 반환했습니다. 인증 또는 라우팅 설정을 확인하세요.');

    const isOAuthCallback = currentPath === AUTH_ROUTES.CALLBACK;
    if (!isOAuthCallback) {
      console.warn('[authApiClient] HTML 응답 - 세션 만료 이벤트 발생');
      safeNotification.error({
        key: 'html-response-error',
        message: '인증 오류',
        description: '잘못된 응답을 받았습니다. 다시 로그인해주세요.',
        placement: NOTIFICATION_CONFIG.PLACEMENT,
      });
      emitSessionExpired();
    } else {
      console.warn('[authApiClient] OAuth 콜백 경로에서 HTML 응답 - 리다이렉트 건너뜀');
    }

    return Promise.reject(error);
  }
  return response;
}, handleApiError);

const realOpenMetadataApiClient = axios.create({
  ...commonAxiosConfig,
  baseURL: '/catalog/api/v1',
});

realOpenMetadataApiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  if (!config.metadata) {
    config.metadata = {};
  }
  config.metadata.startTime = Date.now();
  return config;
});

const SLOW_QUERY_THRESHOLD = 3000; // 3초

const getResponseSize = (data: unknown): number => {
  if (!data) return 0;
  try {
    return new Blob([typeof data === 'string' ? data : JSON.stringify(data)]).size;
  } catch {
    return 0;
  }
};

realOpenMetadataApiClient.interceptors.response.use(
  (response) => {
    const duration = Date.now() - (response.config.metadata?.startTime || 0);
    const responseSize = getResponseSize(response.data);

    if (duration >= SLOW_QUERY_THRESHOLD) {
      performanceMonitor.logMetric('slow_query', {
        url: response.config.url,
        method: response.config.method,
        duration,
        status: response.status,
        responseSize,
      });
    } else {
      performanceMonitor.logSuccessQuery(duration, responseSize);
    }

    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    const duration = Date.now() - (originalRequest.metadata?.startTime || 0);

    const isTimeout = error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT';
    if (isTimeout) {
      const responseSize = getResponseSize(error.response?.data);
      performanceMonitor.logMetric('timeout', {
        url: originalRequest.url,
        method: originalRequest.method,
        duration,
        errorCode: error.code,
        errorMessage: error.message,
        responseSize,
        status: error.response?.status || 'timeout',
        stack: error.stack,
      });
    }

    const isConnectionResetError =
      error.code === 'ECONNRESET' ||
      error.message?.includes('Connection reset') ||
      error.message?.includes('socket hang up');

    const responseMessage = error.response?.data?.message || error.response?.data?.responseMessage || '';
    const isConnectionResetResponse = error.response?.status === 500 && responseMessage.includes('Connection reset');

    if ((isConnectionResetError || isConnectionResetResponse) && !originalRequest._retry) {
      performanceMonitor.logMetric('connection_reset_retry', {
        url: originalRequest.url,
        method: originalRequest.method,
        errorCode: error.code || 'ERR_CONNECTION_RESET',
        errorMessage: error.message,
        originalDuration: duration,
        requestHeaders: originalRequest.headers,
        responseData: error.response?.data,
        responseMessage: responseMessage || undefined,
        status: error.response?.status,
        stack: error.stack,
      });

      originalRequest._retry = true;
      await new Promise((resolve) => setTimeout(resolve, 300));
      return realOpenMetadataApiClient(originalRequest);
    }

    const errorData: Record<string, unknown> = {
      url: originalRequest.url,
      method: originalRequest.method,
      duration,
      status: error.response?.status || 'error',
      errorCode: error.code,
    };

    if (error.response) {
      const status = error.response.status;
      if (status >= 400) {
        const responseSize = getResponseSize(error.response.data);
        errorData.responseDataMeta = getDataMetadata(error.response.data);
        errorData.requestDataMeta = getDataMetadata(originalRequest.data);
        errorData.responseHeaderKeys = sanitizeHeaders(error.response.headers);
        errorData.requestHeaderKeys = sanitizeHeaders(originalRequest.headers);
        errorData.requestParams = originalRequest.params;
        errorData.errorMessage = error.message;
        errorData.responseMessage = responseMessage || undefined;
        errorData.responseSize = responseSize;
        errorData.stack = error.stack;
      }
    } else {
      errorData.errorMessage = error.message;
      errorData.stack = error.stack;
    }

    performanceMonitor.logMetric('api_response_time', errorData);

    if (!axios.isAxiosError(error) || !error.response) {
      return handleApiError(error);
    }

    const status = error.response.status;
    const message = error.response.data?.message || error.message;

    if (status >= HTTP_STATUS.BAD_REQUEST && status < HTTP_STATUS.INTERNAL_SERVER_ERROR && message) {
      const match = message.match(tagMutuallyExclusiveErrorPattern);
      if (match) {
        const tagFullName = match[1].split('and')[0];
        const tagCategoryName = tagFullName.split(KEYBOARD_KEYS.PERIOD)[0];

        const errorMessage = `${tagCategoryName} 분류에서는 하나의 태그만 선택할 수 있습니다. 동일 분류에서 2개 이상의 태그는 동시에 선택할 수 없습니다.`;
        safeNotification.error({
          key: 'tag-mutually-exclusive',
          message: '태그 선택 오류',
          description: errorMessage,
          placement: NOTIFICATION_CONFIG.PLACEMENT,
        });
        return Promise.reject(new Error(errorMessage));
      }
    }

    return handleApiError(error);
  }
);

const realMlflowApiClient = axios.create({
  ...commonAxiosConfig,
  baseURL: '/artifacts',
  timeout: REQUEST_TIMEOUT.DEFAULT,
});

realMlflowApiClient.interceptors.response.use((response) => response, handleApiError);

const realConnectorApiClient = axios.create({
  ...commonAxiosConfig,
  baseURL: '/connector/api',
});

realConnectorApiClient.interceptors.response.use((response) => response, handleApiError);

const realCycloneApiClient = axios.create({
  ...commonAxiosConfig,
  baseURL: '/backend/api/v1',
  paramsSerializer: {
    serialize: (params) => {
      const searchParams = new URLSearchParams();

      Object.entries(params).forEach(([key, value]) => {
        if (value === null || value === undefined) {
          return;
        }

        if (Array.isArray(value)) {
          value.forEach((item) => {
            if (item != null) {
              searchParams.append(key, String(item));
            }
          });
        } else {
          searchParams.append(key, String(value));
        }
      });

      return searchParams.toString();
    },
  },
});

realCycloneApiClient.interceptors.response.use((response) => response, handleApiError);

export const qualityApiClient = (IS_DEMO_MODE
  ? createMockQualityClient()
  : realQualityApiClient) as unknown as AxiosInstance;

export const systemApiClient = (IS_DEMO_MODE
  ? createMockSystemClient()
  : realSystemApiClient) as unknown as AxiosInstance;

export const authApiClient = (IS_DEMO_MODE ? createMockGenericClient() : realAuthApiClient) as unknown as AxiosInstance;

export const openMetadataApiClient = (IS_DEMO_MODE
  ? createMockOpenMetadataClient()
  : realOpenMetadataApiClient) as unknown as AxiosInstance;

export const mlflowApiClient = (IS_DEMO_MODE
  ? createMockMLflowClient()
  : realMlflowApiClient) as unknown as AxiosInstance;

export const connectorApiClient = (IS_DEMO_MODE
  ? createHybridMockClient(createMockConnectorClient(), realConnectorApiClient)
  : realConnectorApiClient) as unknown as AxiosInstance;

export const cycloneApiClient = (IS_DEMO_MODE
  ? createHybridMockClient(createMockCycloneClient(), realCycloneApiClient)
  : realCycloneApiClient) as unknown as AxiosInstance;

export const axiosInstance = authApiClient;
